import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Typography, Grid, Card, CardContent, Button,
  TextField, Chip, Stack, Alert, CircularProgress, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, IconButton, Tooltip, LinearProgress, InputAdornment
} from '@mui/material';
import {
  Telegram, CheckCircle, Warning, Autorenew, Discount,
  ReceiptLong, Logout, OpenInNew, AccessTime, Security,
  ArrowForward, VerifiedUser, HelpOutline
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import { telegramSubscription, auth } from '../services/api';
import useStore from '../store/useStore';
import { openRazorpayCheckout } from '../utils/razorpay';
import PublicFooter from '../components/common/PublicFooter';

export default function UserSubscriberPortal() {
  const navigate = useNavigate();
  const { user, setUser, logout } = useStore();

  const [loading, setLoading] = useState(true);
  const [subData, setSubData] = useState(null);
  const [history, setHistory] = useState([]);
  
  // Coupon state
  const [couponInput, setCouponInput] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState(null);

  // Current order state
  const [currentOrder, setCurrentOrder] = useState(null);
  const [orderLoading, setOrderLoading] = useState(false);

  const loadData = async () => {
    try {
      const [statusRes, histRes] = await Promise.all([
        telegramSubscription.myStatus(),
        telegramSubscription.myHistory(),
      ]);

      if (statusRes.data?.success) {
        setSubData(statusRes.data.data);
      }
      if (histRes.data?.success) {
        setHistory(histRes.data.payments || []);
      }
    } catch (err) {
      console.error('Failed to load subscription data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleApplyCoupon = async () => {
    if (!couponInput.trim()) {
      toast.error('Please enter a coupon code');
      return;
    }

    setCouponLoading(true);
    try {
      const res = await telegramSubscription.applyCoupon(couponInput.trim());
      if (res.data?.success) {
        setAppliedCoupon(res.data.coupon);
        toast.success(res.data.message || 'Coupon applied successfully!');
      }
    } catch (err) {
      console.error('Coupon error:', err);
      toast.error(err.response?.data?.message || 'Invalid or expired coupon');
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponInput('');
    toast.info('Coupon removed');
  };

  const handleInitiateRenewal = async () => {
    setOrderLoading(true);
    try {
      const couponCode = appliedCoupon ? appliedCoupon.code : null;
      const res = await telegramSubscription.createOrder({ couponCode });
      
      if (!res.data?.success) {
        throw new Error(res.data?.message || 'Failed to create payment order');
      }

      const orderData = res.data;
      setCurrentOrder(orderData);

      // Check if 100% discount free coupon
      if (orderData.isFree) {
        await telegramSubscription.verifyPayment({
          razorpayOrderId: orderData.orderId,
          razorpayPaymentId: `free_${Date.now()}`,
          razorpaySignature: 'free_coupon',
        });
        toast.success('Subscription renewed for 1 Month with 100% Discount!');
        await loadData();
        setAppliedCoupon(null);
        setCouponInput('');
        setOrderLoading(false);
        return;
      }

      // Open Official Razorpay Checkout Modal
      await openRazorpayCheckout({
        orderId: orderData.orderId,
        amount: orderData.amount,
        currency: orderData.currency || 'INR',
        keyId: orderData.keyId || orderData.key,
        name: 'AlphaTrading Signals',
        description: '1-Month Telegram Subscription Renewal',
        prefill: {
          name: subData?.user?.fullName || user?.full_name || user?.username || '',
          email: subData?.user?.email || user?.email || '',
          contact: subData?.user?.mobile || user?.mobile_number || '',
        },
        themeColor: '#2563eb',
        onSuccess: async (paymentResponse) => {
          try {
            setOrderLoading(true);
            const verifyRes = await telegramSubscription.verifyPayment({
              razorpayOrderId: paymentResponse.razorpayOrderId,
              razorpayPaymentId: paymentResponse.razorpayPaymentId,
              razorpaySignature: paymentResponse.razorpaySignature,
            });
            toast.success(verifyRes.data?.message || 'Subscription renewed successfully!');
            await loadData();
            setAppliedCoupon(null);
            setCouponInput('');
          } catch (verifyErr) {
            console.error('Payment verification failed:', verifyErr);
            toast.error(verifyErr.response?.data?.message || 'Payment verification failed');
          } finally {
            setOrderLoading(false);
          }
        },
        onDismiss: () => {
          setOrderLoading(false);
          toast('Renewal cancelled. You can retry anytime.', { icon: 'ℹ️' });
        },
        onError: (errorMsg) => {
          setOrderLoading(false);
          toast.error(errorMsg || 'Renewal payment failed');
        },
      });
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Error initiating renewal');
      setOrderLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#060d17' }}>
        <CircularProgress size={48} sx={{ color: '#2563eb' }} />
      </Box>
    );
  }

  const isActive = subData?.isSubscribed;
  const originalPrice = 1000;
  const finalPrice = appliedCoupon ? appliedCoupon.finalPrice : 1000;
  const discountAmount = appliedCoupon ? appliedCoupon.discountAmount : 0;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#060d17', color: '#f8fafc', display: 'flex', flexDirection: 'column' }}>
      {/* Top Navbar */}
      <Box sx={{ borderBottom: '1px solid rgba(255,255,255,0.08)', bgcolor: 'rgba(10,20,35,0.85)', backdropFilter: 'blur(12px)', position: 'sticky', top: 0, zIndex: 100 }}>
        <Container maxWidth="lg">
          <Box sx={{ py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ p: 1, bgcolor: '#2563eb', borderRadius: 2, display: 'flex' }}>
                <Telegram sx={{ color: '#fff', fontSize: 24 }} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#ffffff', lineHeight: 1.1 }}>
                  AlphaTrading Signals
                </Typography>
                <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                  Subscriber Portal
                </Typography>
              </Box>
            </Box>

            <Stack direction="row" spacing={2} alignItems="center">
              <Box sx={{ textAlign: 'right', display: { xs: 'none', sm: 'block' } }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#f8fafc' }}>
                  {subData?.user?.fullName || user?.username || 'Subscriber'}
                </Typography>
                <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                  {subData?.user?.email || user?.email}
                </Typography>
              </Box>

              <Button
                variant="outlined"
                color="inherit"
                size="small"
                startIcon={<Logout />}
                onClick={handleLogout}
                sx={{
                  borderColor: 'rgba(255,255,255,0.2)',
                  color: '#cbd5e1',
                  '&:hover': { borderColor: '#ef4444', color: '#ef4444' },
                }}
              >
                Sign Out
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* Main Content Area */}
      <Container maxWidth="lg" sx={{ py: 5, flex: 1 }}>
        {/* Welcome Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#ffffff', mb: 0.5 }}>
            Welcome, {subData?.user?.fullName || user?.username || 'Member'} 👋
          </Typography>
          <Typography variant="body1" sx={{ color: '#94a3b8' }}>
            Manage your monthly Telegram signals pass, renew subscription, and access live bot alerts.
          </Typography>
        </Box>

        {/* Marketing Slogan & Disclaimer Banner */}
        <Box
          sx={{
            mb: 4,
            p: 2.5,
            borderRadius: 3,
            background: 'linear-gradient(135deg, rgba(37,99,235,0.15) 0%, rgba(16,185,129,0.1) 100%)',
            border: '1px solid rgba(37,99,235,0.3)',
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', md: 'center' },
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 900, color: '#60a5fa', letterSpacing: '0.02em' }}>
              "PAY ₹1,000 AND EARN UNLIMITED"
            </Typography>
            <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mt: 0.5, maxWidth: 750 }}>
              <strong>Risk Disclaimer:</strong> Stock & options trading involves substantial market risk. Past performance and profits are not guaranteed. The ₹1,000 monthly subscription grants exclusive access to real-time notification alerts for educational and informational purposes.
            </Typography>
          </Box>

          <Chip
            label="₹1,000 / Month"
            sx={{
              bgcolor: '#10b981',
              color: '#ffffff',
              fontWeight: 800,
              fontSize: '0.875rem',
              px: 1,
            }}
          />
        </Box>

        <Grid container spacing={3.5}>
          {/* Card 1: Subscription Status & Expiry */}
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                bgcolor: '#0a1626',
                border: isActive ? '1px solid rgba(16,185,129,0.3)' : '1px solid rgba(239,68,68,0.3)',
                borderRadius: 3,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <CardContent sx={{ p: 3, flex: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#ffffff' }}>
                    Subscription Status
                  </Typography>
                  <Chip
                    icon={isActive ? <CheckCircle sx={{ fontSize: '1rem !important' }} /> : <Warning sx={{ fontSize: '1rem !important' }} />}
                    label={isActive ? 'ACTIVE' : 'EXPIRED / INACTIVE'}
                    color={isActive ? 'success' : 'error'}
                    sx={{ fontWeight: 800, px: 1 }}
                  />
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: '#60a5fa' }}>
                    Telegram Trading Signals
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                    Monthly VIP Pass (₹1,000 / Month)
                  </Typography>
                </Box>

                <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', my: 2 }} />

                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                      Subscription Start
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#f8fafc' }}>
                      {formatDate(subData?.startDate)}
                    </Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                      Expiry Date
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: isActive ? '#34d399' : '#f87171' }}>
                      {formatDate(subData?.endDate)}
                    </Typography>
                  </Grid>
                </Grid>

                {isActive && (
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                        Subscription Period Remaining
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#34d399' }}>
                        {subData?.daysRemaining ?? 30} Days Left
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(100, Math.max(5, ((subData?.daysRemaining || 30) / 30) * 100))}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        bgcolor: 'rgba(255,255,255,0.08)',
                        '& .MuiLinearProgress-bar': { bgcolor: '#10b981' },
                      }}
                    />
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Card 2: Telegram Bot Integration */}
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                bgcolor: '#0a1626',
                border: '1px solid rgba(0,136,204,0.3)',
                borderRadius: 3,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <CardContent sx={{ p: 3, flex: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Telegram sx={{ color: '#0088cc', fontSize: 26 }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#ffffff' }}>
                      Telegram Bot Alerts
                    </Typography>
                  </Box>

                  <Chip
                    label={subData?.telegramConnected ? 'CONNECTED' : 'READY TO CONNECT'}
                    sx={{
                      bgcolor: subData?.telegramConnected ? 'rgba(16,185,129,0.2)' : 'rgba(0,136,204,0.2)',
                      color: subData?.telegramConnected ? '#34d399' : '#38bdf8',
                      fontWeight: 700,
                    }}
                  />
                </Box>

                <Typography variant="body2" sx={{ color: '#cbd5e1', mb: 2 }}>
                  Direct automated trading signals, buy/sell targets, and market updates are delivered via our official Telegram bot.
                </Typography>

                <Box sx={{ p: 2, bgcolor: 'rgba(0,136,204,0.08)', borderRadius: 2, border: '1px solid rgba(0,136,204,0.2)', mb: 3 }}>
                  <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mb: 0.5 }}>
                    Bot Username:
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#38bdf8', fontFamily: 'monospace' }}>
                    @{subData?.botUsername || 'JigendraTradingAlerts_bot'}
                  </Typography>
                </Box>

                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  startIcon={<Telegram />}
                  endIcon={<OpenInNew />}
                  href={subData?.telegramRedirectUrl || 'https://t.me/JigendraTradingAlerts_bot'}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    bgcolor: '#0088cc',
                    '&:hover': { bgcolor: '#006699' },
                    py: 1.5,
                    fontWeight: 700,
                    borderRadius: 2.5,
                  }}
                >
                  OPEN TELEGRAM BOT
                </Button>
                <Typography variant="caption" sx={{ color: '#64748b', display: 'block', textAlign: 'center', mt: 1 }}>
                  Click above and tap "START" in Telegram to bind your notifications.
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Card 3: Renew Subscription & Apply Coupon */}
          <Grid item xs={12}>
            <Card
              sx={{
                bgcolor: '#0a1626',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 3,
              }}
            >
              <CardContent sx={{ p: 3.5 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2 }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#ffffff' }}>
                      Renew / Extend Subscription
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                      Extend your signals access by another 30 days. Active days will accumulate seamlessly.
                    </Typography>
                  </Box>

                  <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                    <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block' }}>
                      Total Payable
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                      {appliedCoupon && (
                        <Typography variant="body1" sx={{ color: '#f59e0b', textDecoration: 'line-through' }}>
                          ₹{originalPrice}
                        </Typography>
                      )}
                      <Typography variant="h4" sx={{ fontWeight: 900, color: '#10b981' }}>
                        ₹{finalPrice.toLocaleString('en-IN')}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                        / month
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', mb: 3 }} />

                <Grid container spacing={3} alignItems="center">
                  {/* Coupon Application Box */}
                  <Grid item xs={12} md={7}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#f8fafc', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Discount sx={{ color: '#f59e0b', fontSize: 20 }} />
                      Have a Discount Coupon?
                    </Typography>

                    {appliedCoupon ? (
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          bgcolor: 'rgba(16,185,129,0.15)',
                          border: '1px solid rgba(16,185,129,0.3)',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#34d399' }}>
                            Coupon "{appliedCoupon.code}" Applied!
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#a7f3d0' }}>
                            You save ₹{appliedCoupon.discountAmount} on this renewal.
                          </Typography>
                        </Box>
                        <Button
                          size="small"
                          color="error"
                          onClick={handleRemoveCoupon}
                          sx={{ textTransform: 'none', fontWeight: 600 }}
                        >
                          Remove
                        </Button>
                      </Box>
                    ) : (
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                        <TextField
                          size="small"
                          placeholder="e.g. WELCOME20, FLAT200"
                          value={couponInput}
                          onChange={(e) => setCouponInput(e.target.value)}
                          sx={{
                            flex: 1,
                            '& .MuiOutlinedInput-root': {
                              color: '#fff',
                              bgcolor: 'rgba(255,255,255,0.05)',
                              '& fieldset': { borderColor: 'rgba(255,255,255,0.15)' },
                              '&:hover fieldset': { borderColor: '#2563eb' },
                            },
                          }}
                        />
                        <Button
                          variant="contained"
                          onClick={handleApplyCoupon}
                          disabled={couponLoading}
                          sx={{
                            bgcolor: '#f59e0b',
                            color: '#000',
                            fontWeight: 700,
                            '&:hover': { bgcolor: '#d97706' },
                            px: 3,
                          }}
                        >
                          {couponLoading ? <CircularProgress size={20} color="inherit" /> : 'Apply Coupon'}
                        </Button>
                      </Stack>
                    )}
                  </Grid>

                  {/* Renew Button */}
                  <Grid item xs={12} md={5}>
                    <Button
                      variant="contained"
                      fullWidth
                      size="large"
                      onClick={handleInitiateRenewal}
                      disabled={orderLoading}
                      startIcon={orderLoading ? <CircularProgress size={20} color="inherit" /> : <Autorenew />}
                      sx={{
                        py: 1.8,
                        fontWeight: 800,
                        fontSize: '1rem',
                        bgcolor: '#2563eb',
                        '&:hover': { bgcolor: '#1d4ed8' },
                        borderRadius: 2.5,
                        boxShadow: '0 8px 24px rgba(37,99,235,0.35)',
                      }}
                    >
                      {orderLoading ? 'Preparing Checkout...' : `Renew Subscription • Pay ₹${finalPrice}`}
                    </Button>
                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block', textAlign: 'center', mt: 1 }}>
                      Powered by Razorpay Secure Payment Gateway
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Card 4: Payment History */}
          <Grid item xs={12}>
            <Card sx={{ bgcolor: '#0a1626', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <ReceiptLong sx={{ color: '#94a3b8' }} />
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#ffffff' }}>
                    Payment & Invoice History
                  </Typography>
                </Box>

                {history.length === 0 ? (
                  <Box sx={{ py: 4, textAlign: 'center' }}>
                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                      No payment transactions recorded yet.
                    </Typography>
                  </Box>
                ) : (
                  <TableContainer component={Paper} sx={{ bgcolor: 'transparent', boxShadow: 'none' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ color: '#64748b', fontWeight: 700 }}>Date</TableCell>
                          <TableCell sx={{ color: '#64748b', fontWeight: 700 }}>Plan</TableCell>
                          <TableCell sx={{ color: '#64748b', fontWeight: 700 }}>Order Reference</TableCell>
                          <TableCell sx={{ color: '#64748b', fontWeight: 700 }}>Payment ID</TableCell>
                          <TableCell sx={{ color: '#64748b', fontWeight: 700 }}>Amount</TableCell>
                          <TableCell sx={{ color: '#64748b', fontWeight: 700 }}>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {history.map((tx) => (
                          <TableRow key={tx.id} sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.02)' } }}>
                            <TableCell sx={{ color: '#cbd5e1' }}>{formatDate(tx.date)}</TableCell>
                            <TableCell sx={{ color: '#f8fafc', fontWeight: 600 }}>Telegram Monthly</TableCell>
                            <TableCell sx={{ color: '#94a3b8', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                              {tx.orderId || 'N/A'}
                            </TableCell>
                            <TableCell sx={{ color: '#60a5fa', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                              {tx.paymentId || 'N/A'}
                            </TableCell>
                            <TableCell sx={{ color: '#10b981', fontWeight: 700 }}>
                              ₹{tx.amount}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={tx.status}
                                size="small"
                                color={tx.status === 'COMPLETED' ? 'success' : 'default'}
                                sx={{ fontWeight: 700, fontSize: '0.7rem' }}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Public Footer */}
      <PublicFooter />
    </Box>
  );
}
